<template>
  <div class="convey-dashboard d-flex flex-column gap-3">
    <div class="row d-flex align-items-center">
      <div class="convey-dashboard--header d-flex align-items-center justify-content-end">
        <ConveyDatePicker
          placeholder="Select Date Range"
          @changeDatePicker="handleChangeDatePicker"
          :isDarkMode="isDarkMode"
        />
      </div>
    </div>
    <b-overlay
      :show="(isLoading || isGeoMapLoading)"
      rounded="sm"
      no-wrap
    ></b-overlay>
    <div class="convey-dashboard--blocks row d-flex">
      <div class="col-md-12 col-xl-7">
        <Statistics :statsCount="statsCount" :isDarkMode="isDarkMode" />
      </div>
      <div class="col-md-12 col-xl-5 mb-3">
        <TranslationGraph
          :translationData="translationData"
          :isDarkMode="isDarkMode"
          :isLoading="isLoading"
        />
      </div>

      <div class="col-md-12 col-xl-7 mb-3">
        <ConversationActivityGraph
          :conversationActivityData="conversationActivityData"
          :isDarkMode="isDarkMode"
        />
      </div>
      <div class="col-md-12 col-xl-5 mb-3" style="min-height: 450px">
        <GeolocationActivity
          :geolocationActivityData="geolocationActivityData"
          :isDarkMode="isDarkMode"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Statistics from "../dashboard/Statistics.vue";
import TranslationGraph from "../dashboard/TranslationGraph.vue";
import ConversationActivityGraph from "../dashboard/ConversationActivityGraph.vue";
import GeolocationActivity from "../geolocation-activity/GeolocationActivity.vue";
import ConveyDatePicker from "../common/DatePicker.vue";
import {
  makeAgentStatsQuery,
  makeAgentGeoLocationStatsQuery,
} from "../../graphql/queries";
import { runGraphqlQuery } from "../../graphql/utils";
import moment from "moment";
import { mapGetters, mapState } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";

export default {
  name: "Dashboard",
  mixins: [formatter, errorHandler],
  components: {
    Statistics,
    TranslationGraph,
    ConversationActivityGraph,
    GeolocationActivity,
    ConveyDatePicker,
  },
  data() {
    return {
      dateRange: {
        start_date: moment().startOf("year").format("YYYY-MM-DD"),
        end_date: moment().endOf("year").format("YYYY-MM-DD"),
      },
      statsCount: {
        conversations: null,
        averageDuration: null,
        messages: null,
        failedIncompleteMsgs: null,
        locationRequest: null,
        videoSessions: null,
      },
      translationData: null,
      conversationActivityData: null,
      geolocationActivityData: null,
      isLoading: false,
      showDashboard: false,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode"]),
    ...mapGetters("agency", ["currentAgencyId", "agencySettings"]),
    ...mapState({
      currentAgency: (state) => state.agency.agency,
    }),
    ...mapGetters("user", ["currentUser", "isMobile"]),
    currentAgencyName() {
      if (this.currentAgency)
        return this.currentAgency ? this.currentAgency.agency_name : "";

      return "";
    },
  },
  created() {
    const showDashboard = localStorage.getItem("showDashboard");

    if (showDashboard) {
      this.showDashboard = showDashboard === "false" ? false : true;
    }

    this.fetchOperations();
  },
  watch: {
    dateRange(newVal, oldVal) {
      if (newVal != oldVal) {
        this.fetchOperations();
      }
    },
  },
  methods: {
    fetchOperations() {
      this.fetchAbortController = new AbortController();
      this.fetchAllData();
      this.fetchGeoLocationData();
    },
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    handleChangeDatePicker(data) {
      if (data.start && data.end) {
        this.dateRange = {
          start_date: data.start,
          end_date: data.end,
        };
      } else {
        this.dateRange = null;
      }
    },
    handleChangeShowDashboard(newVal) {
      localStorage.setItem("showDashboard", newVal);
    },
    async fetchAllData() {
      try {
        this.isLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }
        const query = makeAgentStatsQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortController.signal)
        const responseData = res.data.data.getAgentStats;

        this.statsCount = {
          conversations: this.formatNumber(responseData.conversationCount),
          averageDuration: this.formatDuration(responseData.averageDuration),
          messages: {
            ...responseData.messageCount,
            totalCount: this.formatNumber(
              responseData.messageCount.totalCount
            ),
            mmsCount: this.formatNumber(responseData.messageCount.mmsCount),
            smsCount: this.formatNumber(responseData.messageCount.smsCount),
            othersCount: this.formatNumber(
              responseData.messageCount.othersCount
            ),
          },
          failedIncompleteMsgs: this.formatNumber(
            responseData.failedMessagesPercentage
          ),
          locationRequest: {
            ...responseData.geolocationRequestCount,
            count: this.formatNumber(
              responseData.geolocationRequestCount.count
            ),
          },
          videoSessions: {
            ...responseData.videoSessions,
            count: this.formatNumber(responseData.videoSessions.count),
            average: this.formatNumber(
              parseInt(responseData.videoSessions.average)
            ),
          },
        };
        this.translationData = responseData.translationStats;
        this.conversationActivityData = responseData.conversationActivity;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[ConveyCommunicatorDashboard:fetchAllData] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGeoLocationData() {
      try {
        this.isGeoMapLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const query = makeAgentGeoLocationStatsQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const responseData = res.data.data.getAgentStats;
        this.geolocationActivityData = responseData.geolocationActivity;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[ConveyCommunicatorDashboard:fetchGeoLocationData] ERROR: ", err);
        }
      } finally {
        this.isGeoMapLoading = false;
      }
    },
  },
  beforeDestroy() {
    this.abortFetchOperations();
  }
};
</script>

<style lang="scss">
.convey-dashboard {
  width: 100%;
  height: 100%;
  &--blocks {
    &.row {
      margin: 0 -0.5rem !important;
      > * {
        padding: 0 0.5rem !important;
      }
    }
  }
  &--page-title {
    color: #000000;
    font-size: 1.25rem;
    font-weight: 500;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
